import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { CATEGORIES, PROJECTS, IMAGES, projectsIn, projectPath, getCategory } from "@/data/site";
import { MaskLines, FadeUp, EASE } from "@/components/Reveal";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { Marquee } from "@/components/Marquee";
import { ArrowLink } from "@/components/ArrowLink";
import { SectionTitle, Ornament } from "@/components/SectionTitle";
import { Seo } from "@/components/Seo";
import { HistoryTeaser } from "@/components/TobagoHistory";
import { SpringShowcase } from "@/components/SpringShowcase";
import { FerryShowcase } from "@/components/FerryShowcase";

export const GoldButton = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative inline-flex items-center gap-3 overflow-hidden border border-gold-deep bg-gold-deep px-7 py-4 font-caps text-[12px] tracking-[0.24em] uppercase text-ivory transition-colors duration-500 hover:bg-heritage hover:border-heritage"
  >
    {children}
    <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
  </Link>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} className="relative pt-[72px] lg:pt-[88px]" data-testid="home-hero">
      <div className="relative lg:h-[calc(100svh-88px)] lg:min-h-[620px] lg:max-h-[880px]">
        <div className="relative h-[58svh] overflow-hidden bg-heritage-deep lg:absolute lg:inset-0 lg:h-full">
          <motion.video
            poster="/images/hero-bay-hd.webp"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label="Waves rolling onto a golden beach framed by rainforest"
            data-testid="home-hero-video"
            style={{ y, scale: 1.12 }}
            className="absolute inset-0 h-full w-full object-cover object-center"
          >
            <source src="/images/hero-waves.webm" type="video/webm" />
            <source src="/images/hero-waves.mp4" type="video/mp4" />
          </motion.video>
          <div className="absolute inset-0 hidden bg-gradient-to-r from-ivory via-ivory/80 to-transparent lg:block lg:w-[62%]" />
        </div>
        <div className="relative mx-auto flex h-full max-w-[1440px] items-center px-5 py-12 md:px-10 lg:absolute lg:inset-0 lg:py-0">
          <div className="max-w-xl">
            <motion.p
              className="font-caps text-[12px] leading-relaxed tracking-[0.28em] uppercase text-gold-deep"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
              data-testid="home-hero-location"
            >
              Tobago, Trinidad & Tobago <span className="mx-2 hidden text-gold sm:inline">|</span><span className="block sm:inline">Est. 1777</span>
            </motion.p>
            <Ornament className="mt-3 !h-4 !w-7" />
            <h1 className="sr-only" data-testid="home-hero-title">Little Englishman's Bay Estate</h1>
            <MaskLines
              as="p"
              lines={["A Heritage Estate.", "A Living Landscape.", "An Enduring Vision."]}
              delay={0.35}
              className="mt-4 font-caps text-[2.1rem] leading-[1.12] text-heritage sm:text-5xl lg:text-[3.4rem]"
              testid="home-hero-subtitle"
            />
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-heritage/85" data-testid="hero-intro-paragraph">
                Little Englishman’s Bay Estate embodies the timeless beauty, rich heritage, and extraordinary natural character of Tobago. Established in 1777, the estate is entering a new chapter, thoughtfully bringing together refined hospitality, rainforest conservation, cultural discovery, and sustainable development. Our vision is to create an exceptional Caribbean destination where history is honored, nature is preserved, and generations to come can experience the enduring spirit of this remarkable place.
              </p>
              <div className="mt-8"><GoldButton to="/estate" testid="hero-discover-estate-button">Discover the Estate</GoldButton></div>
            </motion.div>
          </div>
        </div>
      </div>
      <div className="h-px bg-gradient-to-r from-gold/20 via-gold to-gold/20" />
    </section>
  );
};

const AreaCard = ({ cat, i }) => (
  <FadeUp delay={i * 0.08} className="px-0 sm:px-4 lg:px-5">
    <Link to={`/${cat.slug}`} className="group block text-center" data-testid={`category-card-${cat.slug}`}>
      <Photo
        image={cat.cardImage ?? cat.image}
        alt={cat.title}
        playInView
        testid={`category-card-media-${cat.slug}`}
        className="aspect-[4/3]"
        imgClassName="transition-transform duration-[1.4s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.07]"
        tagClassName="left-2 bottom-2 !text-[10px] !px-2"
      />
      <p className="mt-5 font-caps text-[11px] tracking-[0.3em] text-gold-deep">{cat.numeral}</p>
      <h3 className="mt-1 font-caps text-lg leading-snug text-heritage transition-colors group-hover:text-gold-deep">{cat.title}</h3>
      <p className="mx-auto mt-2 max-w-[16rem] text-[15px] leading-snug text-muted-ink">{projectsIn(cat.slug).map((p) => p.name).join(" · ")}</p>
      <span className="mt-4 inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.24em] uppercase text-gold-deep">
        Learn more <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1.5" />
      </span>
    </Link>
  </FadeUp>
);

const EstateAreas = () => (
  <section className="relative overflow-hidden py-20 lg:py-24" data-testid="home-categories">
    <img src="/images/decor-palms.webp" alt="" aria-hidden="true" className="pointer-events-none absolute -left-10 top-0 hidden w-[340px] opacity-[0.16] lg:block" />
    <img src="/images/decor-compass.webp" alt="" aria-hidden="true" className="pointer-events-none absolute -right-16 top-4 hidden w-[300px] opacity-[0.14] lg:block" />
    <div className="relative mx-auto max-w-[1440px] px-5 md:px-10">
      <FadeUp>
        <SectionTitle eyebrow="The Estate" title="Land, Heritage & Living Landscape">
          Little Englishman's Bay Estate is a historic estate on the island of Tobago, where coastline, rainforest, rivers and cultivated land meet within a single, remarkably diverse landscape. Its projects, some existing and many in development or planning, are gathered into six areas.
        </SectionTitle>
      </FadeUp>
      <div className="mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-0 lg:gap-y-14 lg:divide-x lg:divide-gold/40 lg:[&>*:nth-child(3n+1)]:!border-l-0">
        {CATEGORIES.map((c, i) => <AreaCard key={c.slug} cat={c} i={i} />)}
      </div>
    </div>
  </section>
);

const Ledger = () => (
  <section className="bg-ivory-muted py-20 lg:py-24" data-testid="home-projects-ledger">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <FadeUp><SectionTitle eyebrow="Register of Projects" title="All Twelve, at a Glance" /></FadeUp>
      <ul className="mt-12 grid lg:grid-cols-2 lg:gap-x-12">
        {PROJECTS.map((p, i) => (
          <FadeUp key={p.slug} delay={0.03 * i} y={16}>
            <Link
              to={projectPath(p)}
              className="group grid grid-cols-[2.5rem_1fr_auto] items-center gap-x-4 gap-y-2 border-t border-gold/30 py-5 transition-colors duration-500 hover:bg-heritage sm:grid-cols-[2.5rem_1fr_auto_auto] md:px-4"
              data-testid={`ledger-row-${p.slug}`}
            >
              <span className="font-caps text-sm text-gold-deep group-hover:text-gold">{String(i + 1).padStart(2, "0")}</span>
              <span>
                <span className="block font-display text-2xl text-heritage transition-[color,transform] duration-500 group-hover:translate-x-2 group-hover:text-ivory">{p.name}</span>
                <span className="mt-1 block text-sm text-muted-ink group-hover:text-ivory/75">{getCategory(p.category).title}</span>
              </span>
              <span className="col-start-2 row-start-2 sm:col-start-auto sm:row-start-auto">
                <span className="group-hover:hidden"><StatusBadge status={p.status} testid={`ledger-status-${p.slug}`} /></span>
                <span className="hidden group-hover:inline"><StatusBadge status={p.status} dark /></span>
              </span>
              <ArrowUpRight className="col-start-3 row-span-2 row-start-1 text-gold-deep transition-transform duration-500 group-hover:rotate-45 group-hover:text-gold sm:col-start-auto sm:row-span-1 sm:row-start-auto" />
            </Link>
          </FadeUp>
        ))}
      </ul>
    </div>
  </section>
);

const EstateTeaser = () => (
  <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 lg:py-24" data-testid="home-estate-teaser">
    <div className="grid items-center gap-14 lg:grid-cols-12">
      <FadeUp className="lg:col-span-5">
        <div className="border border-gold/50 p-3">
          <img src="/images/estate-map.webp" alt="Topographic map of Little Englishman's Bay and Englishman's Bay, Tobago, showing Riversdale (213 m), rivers and contours" loading="lazy" className="block h-auto w-full" data-testid="estate-teaser-map" />
        </div>
      </FadeUp>
      <div className="lg:col-span-6 lg:col-start-7">
        <FadeUp>
          <Ornament />
          <p className="mt-3 font-caps text-[12px] tracking-[0.32em] uppercase text-gold-deep">The Estate</p>
          <h2 className="mt-4 font-caps text-3xl leading-tight text-heritage md:text-[2.7rem]">From the Forest to the Shore</h2>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-ink">
            Coastline, rainforest, rivers, waterfalls and agricultural land lie side by side on the estate. Learn about its heritage, its landscape and the vision that guides its future.
          </p>
          <div className="mt-9"><GoldButton to="/estate" testid="teaser-estate-link">Read about the Estate</GoldButton></div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const ContactBand = () => (
  <section className="relative overflow-hidden bg-heritage py-20 text-center lg:py-24" data-testid="home-contact-band">
    <img src="/images/shield.webp" alt="" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 w-[380px] -translate-x-1/2 -translate-y-1/2 opacity-[0.07]" />
    <FadeUp className="relative px-5">
      <SectionTitle eyebrow="Enquiries" title="Correspondence is Welcome" light>
        For enquiries about the estate and its projects, visit our contact page.
      </SectionTitle>
      <div className="mt-10 flex justify-center"><ArrowLink to="/contact" light testid="contact-band-link">Contact the Estate</ArrowLink></div>
    </FadeUp>
  </section>
);

export default function Landing() {
  return (
    <>
      <Seo
        path="/"
        description="Little Englishman's Bay Estate, Tobago, Est. 1777: a heritage estate of coastline, rainforest, rivers and cocoa, home to evolving hospitality and conservation projects."
      />
      <Hero />
      <EstateAreas />
      <HistoryTeaser />
      <Marquee />
      <SpringShowcase />
      <FerryShowcase />
      <Ledger />
      <EstateTeaser />
      <ContactBand />
    </>
  );
}
